import React from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { ChartRendererProps } from '../../types/analytics';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const ChartRenderer: React.FC<ChartRendererProps> = ({
  chartType,
  data,
  xKey,
  yKey,
  loading = false,
}) => {
  if (loading) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 h-80 flex items-center justify-center">
        <div className="animate-pulse text-gray-400">Loading chart...</div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 h-80 flex items-center justify-center">
        <p className="text-gray-400">No data available</p>
      </div>
    );
  }

  // Auto-detect keys if not provided
  const detectedXKey = xKey || Object.keys(data[0])[0];
  const detectedYKey = yKey || Object.keys(data[0])[1];

  const renderBarChart = () => (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
        <XAxis dataKey={detectedXKey} stroke="#9CA3AF" />
        <YAxis stroke="#9CA3AF" />
        <Tooltip
          contentStyle={{
            backgroundColor: '#1F2937',
            border: '1px solid #374151',
            borderRadius: '0.5rem',
            color: '#F3F4F6',
            padding: '12px',
          }}
          itemStyle={{
            color: '#F9FAFB',
            fontSize: '14px',
            fontWeight: '500',
          }}
          labelStyle={{
            color: '#F9FAFB',
            fontSize: '14px',
            fontWeight: '600',
            marginBottom: '4px',
          }}
        />
        <Legend wrapperStyle={{ color: '#9CA3AF' }} />
        <Bar dataKey={detectedYKey} fill="#3B82F6" />
      </BarChart>
    </ResponsiveContainer>
  );

  const renderLineChart = () => (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
        <XAxis dataKey={detectedXKey} stroke="#9CA3AF" />
        <YAxis stroke="#9CA3AF" />
        <Tooltip
          contentStyle={{
            backgroundColor: '#1F2937',
            border: '1px solid #374151',
            borderRadius: '0.5rem',
            color: '#F3F4F6',
            padding: '12px',
          }}
          itemStyle={{
            color: '#F9FAFB',
            fontSize: '14px',
            fontWeight: '500',
          }}
          labelStyle={{
            color: '#F9FAFB',
            fontSize: '14px',
            fontWeight: '600',
            marginBottom: '4px',
          }}
        />
        <Legend wrapperStyle={{ color: '#9CA3AF' }} />
        <Line type="monotone" dataKey={detectedYKey} stroke="#10B981" strokeWidth={2} />
      </LineChart>
    </ResponsiveContainer>
  );

  const renderPieChart = () => (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={data}
          dataKey={detectedYKey}
          nameKey={detectedXKey}
          cx="50%"
          cy="50%"
          outerRadius={100}
          label
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={{
            backgroundColor: '#1F2937',
            border: '1px solid #374151',
            borderRadius: '0.5rem',
            color: '#F3F4F6',
            padding: '12px',
          }}
          itemStyle={{
            color: '#F9FAFB',
            fontSize: '14px',
            fontWeight: '500',
          }}
          labelStyle={{
            color: '#F9FAFB',
            fontSize: '14px',
            fontWeight: '600',
            marginBottom: '4px',
          }}
        />
        <Legend wrapperStyle={{ color: '#9CA3AF' }} />
      </PieChart>
    </ResponsiveContainer>
  );

  const renderTable = () => {
    const keys = Object.keys(data[0]);

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-700">
          <thead className="bg-gray-700">
            <tr>
              {keys.map((key) => (
                <th
                  key={key}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider"
                >
                  {key}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-gray-800 divide-y divide-gray-700">
            {data.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {keys.map((key) => (
                  <td key={key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    {row[key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
      {chartType === 'bar' && renderBarChart()}
      {chartType === 'line' && renderLineChart()}
      {chartType === 'pie' && renderPieChart()}
      {chartType === 'table' && renderTable()}
    </div>
  );
};

export default ChartRenderer;
